import '../text-and-lang/as-text.ts';
import '../base/buttons/as-button.ts';
import '../base/as-op-status.ts';
import type { AsOpStatus } from '../base/as-op-status.ts';
import { wireDownloadButton, DOWNLOAD_DISABLED_TOOLTIP, type DownloadWiring } from './download-wiring.ts';
import { ExportBuilder } from '../../logic/importExport/exportBuilder.ts';
import { liveAppData } from '../../store/store.ts';
import paramGroupStyleText from '../parameters/as-param-group.css?raw';

/**
 * <as-export-panel>
 *
 * Self-contained export card. Renders as a param-group-style card containing
 * the registered export action buttons. Designed to be dropped directly into
 * an <as-step-panel>. A default <slot> sits below the buttons for optional
 * extra content (e.g. a dev "Inspect builder" button).
 *
 * The grabcad button is shown automatically when liveAppData.appEnv === 'grabcad'.
 *
 * Attributes
 *   subheading-default-text / subheading-dict-key  — card heading
 *   exporter-id                                    — forwarded to all buttons (default: 'default')
 *   disabled                                       — forwarded to all buttons
 *   filename                                       — download filename (including extension)
 *   download-name / download-default-text / download-dict-key
 *   download-pending-default-text / download-pending-dict-key
 *   package-name                                   — forwarded to as-grabcad-export-button
 *   grabcad-name / grabcad-default-text / grabcad-dict-key
 *   grabcad-pending-default-text / grabcad-pending-dict-key
 *
 * Events
 *   as-button-click  composed, bubbles — forwarded from inner buttons
 *
 * Methods
 *   reset()  — clears all button statuses
 */
export class AsExportPanel extends HTMLElement {
  static readonly observedAttributes = [
    'subheading-default-text', 'subheading-dict-key',
    'exporter-id', 'disabled',
    'filename',
    'download-name', 'download-default-text', 'download-dict-key',
    'download-pending-default-text', 'download-pending-dict-key',
    'package-name',
    'grabcad-name', 'grabcad-default-text', 'grabcad-dict-key',
    'grabcad-pending-default-text', 'grabcad-pending-dict-key',
  ];

  private static readonly _sheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(
      paramGroupStyleText + `
.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}
:host(:not([subheading-default-text]):not([subheading-dict-key])) .header {
  display: none;
}`
    );
    return s;
  })();

  #removeEnvEffect: (() => void) | null = null;
  #removeCanDownloadEffect: (() => void) | null = null;
  #downloadWiring: DownloadWiring | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsExportPanel._sheet];
    this._render();
  }

  connectedCallback(): void {
    this.#removeEnvEffect = liveAppData.addSideEffect('appEnv', () => this._render());
    this.#removeCanDownloadEffect = liveAppData.addSideEffect('canDownload', () => this._render());
    this._render();
  }

  disconnectedCallback(): void {
    this.#removeEnvEffect?.();
    this.#removeEnvEffect = null;
    this.#removeCanDownloadEffect?.();
    this.#removeCanDownloadEffect = null;
    this.#downloadWiring?.dispose();
    this.#downloadWiring = null;
  }

  attributeChangedCallback(_name: string, old: string | null, next: string | null): void {
    if (old === next) return;
    this._render();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Reset all button statuses back to idle. */
  reset(): void {
    this.#downloadWiring?.reset();
    this.shadowRoot?.querySelectorAll('*').forEach((el: any) => {
      if (typeof el.reset === 'function') el.reset();
    });
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private _esc(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private _render(): void {
    const env        = liveAppData.appEnv ?? '';
    const exporterId = this.getAttribute('exporter-id') ?? 'default';
    const disabled   = this.hasAttribute('disabled') ? ' disabled' : '';

    // ── Subheading ────────────────────────────────────────────────────────────
    const dictKey     = this.getAttribute('subheading-dict-key');
    const defaultText = this.getAttribute('subheading-default-text') ?? '';
    const subheadingHtml = dictKey
      ? `<as-text dict-key="${this._esc(dictKey)}" default-text="${this._esc(defaultText)}"></as-text>`
      : defaultText ? `<as-text>${this._esc(defaultText)}</as-text>` : '';

    // ── Plugin buttons ────────────────────────────────────────────────────────
    const buttonParts: string[] = [];

    for (const desc of ExportBuilder.getButtonDescriptors()) {
      if (desc.requiredEnv && env !== desc.requiredEnv) continue;

      const prefix = desc.panelPrefix ? desc.panelPrefix + '-' : '';
      let attrs = ` exporter-id="${this._esc(exporterId)}"${disabled}`;

      for (const key of ['name', 'default-text', 'dict-key', 'pending-default-text', 'pending-dict-key'] as const) {
        const val = this.getAttribute(prefix + key);
        if (val !== null) attrs += ` ${key}="${this._esc(val)}"`;
      }

      for (const attr of desc.extraAttrs ?? []) {
        const val = this.getAttribute(attr);
        if (val !== null) attrs += ` ${attr}="${this._esc(val)}"`;
      }

      buttonParts.push(`<${desc.tagName}${attrs}></${desc.tagName}>`);
    }

    // ── Download button — always last ─────────────────────────────────────────
    const canDownload = liveAppData.canDownload !== false;
    let dlAttrs = `${canDownload ? disabled : ' disabled'} no-default-icon`;
    if (!canDownload) dlAttrs += ` tooltip-default-text="${this._esc(DOWNLOAD_DISABLED_TOOLTIP)}" tooltip-placement="top"`;
    for (const key of ['name', 'default-text', 'dict-key'] as const) {
      const val = this.getAttribute('download-' + key);
      if (val !== null) dlAttrs += ` ${key}="${this._esc(val)}"`;
    }
    buttonParts.push(`<as-button class="download-btn"${dlAttrs}></as-button><as-op-status class="download-status"></as-op-status>`);

    this.shadowRoot!.innerHTML = /*html*/`
      <div class="header">
        <h3 class="subheading">${subheadingHtml}</h3>
      </div>
      <div class="content">
        <div class="buttons">${buttonParts.join('')}</div>
        <slot></slot>
      </div>`;

    // The shadow DOM was just replaced, so the previous wiring points at a dead
    // button — rewire against the fresh elements.
    this.#downloadWiring?.dispose();
    const dlBtn = this.shadowRoot!.querySelector('.download-btn') as HTMLElement | null;
    this.#downloadWiring = dlBtn ? wireDownloadButton({
      button: dlBtn,
      status: () => this.shadowRoot?.querySelector('.download-status') as AsOpStatus | null,
      getExporter: () => (document.querySelector('as-app') as any)?.exporter ?? null,
      getFilename: () => this.getAttribute('filename') || undefined,
      getExporterId: () => this.getAttribute('exporter-id') ?? 'default',
      getPendingLabel: () => ({
        defaultText: this.getAttribute('download-pending-default-text') ?? 'Downloading...',
        dictKey: this.getAttribute('download-pending-dict-key'),
      }),
    }) : null;
  }
}

if (!customElements.get('as-export-panel')) {
  customElements.define('as-export-panel', AsExportPanel);
} else {
  console.warn('as-export-panel already defined');
}

